import type { Document } from '@packages/domain/models/document';
import {
  LRS_DOCUMENT_TYPE,
  NOT_FAILED_FILE_STATUSES,
} from 'constants/documents';
import type { RootState } from 'reducers/types';
import {
  isCancelled,
  isDelivered,
  isReceived,
} from 'selectors/entities/payment/payment';

const AVAILABLE_FILE_STATUS = 'available';
const VOID_FILE_STATUS = 'void';
const DOC_VERIFY_WITH_ERRORS_STATUS = 'doc_verify_with_errors';
const DOC_VERIFY_IN_PROGRESS = 'doc_verify_in_progress';
const AVAILABLE_STATUSES = [
  AVAILABLE_FILE_STATUS,
  VOID_FILE_STATUS,
  DOC_VERIFY_WITH_ERRORS_STATUS,
];

export const documentsCollection = (state: RootState) => {
  const {
    entities: { documentsCollection },
  } = state;

  return documentsCollection;
};

export const isDocVerifyActive = (state: RootState) => {
  const {
    entities: {
      documentsCollection: { docVerifyActive = false },
    },
  } = state;

  return docVerifyActive;
};

export const hasDocumentsCollection = (state: RootState) => {
  const {
    entities: { documentsCollection },
  } = state;

  return Object.keys(documentsCollection).length > 0;
};

export const hasLRSInDocumentCollection = (state: RootState) => {
  if (isCancelled(state)) {
    return false;
  }

  const {
    entities: { documentsCollection },
  } = state;

  return documentsCollection?.documents?.some(
    d => d.type === LRS_DOCUMENT_TYPE,
  );
};

export const allRequiredFilesUploaded = (state: RootState) => {
  const hasValidFiles = (document: Document) => {
    const { files } = document;

    return files.some(file => NOT_FAILED_FILE_STATUSES.includes(file.status));
  };

  const { documents } = documentsCollection(state);
  const requiredDocuments = documents?.filter(document => document.required);

  return requiredDocuments?.every(document => hasValidFiles(document));
};

export const requiresDocuments = (state: RootState) => {
  if (isReceived(state) || isDelivered(state) || isCancelled(state)) {
    return false;
  }

  return hasDocumentsCollection(state);
};

export const allFilesAnalyzed = (state: RootState) => {
  const {
    entities: { documentsCollection },
  } = state;

  const documents = documentsCollection.documents;

  if (!documents) {
    return true;
  }

  const allFilesAvailable = documents.some(document => {
    const { files } = document;
    return files.some(file => !AVAILABLE_STATUSES.includes(file.status));
  });

  return !allFilesAvailable;
};

export const hasFilesInDocVerifyProcess = (state: RootState) => {
  const {
    entities: { documentsCollection },
  } = state;

  const documents = documentsCollection?.documents;

  if (!Array.isArray(documents)) {
    return false;
  }

  return documents.some(document => {
    const { files } = document;
    return files.some(file => file.status === DOC_VERIFY_IN_PROGRESS);
  });
};
